import {
  Host,
  Input,
  OnInit,
  Optional,
  SkipSelf,
  ViewChild,
  Component,
  ElementRef,
  forwardRef,
  ViewEncapsulation,
} from '@angular/core';
import { ControlContainer, NG_VALUE_ACCESSOR } from '@angular/forms';

import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { MatDatepicker, MatDatepickerInputEvent } from '@angular/material/datepicker';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';

import * as moment from 'moment';

import {BaseControlComponent, DatepickerControl, Helper} from '../../classes';

import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-datepicker',
  templateUrl: Helper.getPortalTemplate(),
  styleUrls: [Helper.getPortalStyle()],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatepickerComponent),
      multi: true,
    },
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },

    {
      provide: MAT_DATE_FORMATS, useValue: {
        parse: {
          dateInput: environment.datePickerFormat,
        },
        display: {
          dateInput: environment.datePickerFormat,
          monthYearLabel: 'MMM YYYY',
          dateA11yLabel: environment.datePickerFormat,
          monthYearA11yLabel: 'MMMM YYYY',
        },
      },
    },
  ],
  encapsulation: ViewEncapsulation.None,
})
export class DatepickerComponent
  extends BaseControlComponent<string | number | null, MatDatepickerInputEvent<moment.Moment>, DatepickerControl> implements OnInit {

  @Input() public disabled: boolean;
  @Input() public outlined: boolean;

  @ViewChild('datepickerInput', { static: true }) public datepickerInputRef: ElementRef | undefined;
  @ViewChild('datepicker', { static: true }) public datepickerRef: MatDatepicker<moment.Moment> | undefined;

  constructor(
    @Optional() @Host() @SkipSelf()
    protected controlContainer: ControlContainer,
  ) {
    super(controlContainer);

    this.disabled = false;
    this.outlined = false;
  }

  protected setDate(value: moment.Moment): string {
    return value.utc(true)
      .toISOString();
  }

  ngOnInit(): void {
    this.asFormsControl();
    this.checkInvalid();
  }

  openDatepicker(datepicker: MatDatepicker<any>): void {
    datepicker.opened = !datepicker.opened;
  }

  resetDatepicker(): void {
    if (this.datepickerInputRef) {
      this.datepickerInputRef.nativeElement.value = null;
    }

    if (this.control) {
      this.control.value = null;
      this.control.resetErrors();
    }

    if (this.onChange) {
      this.onChange(null);
    }

    this.checkInvalid();

    super.writeValue(null);
  }

  changeValue(event: MatDatepickerInputEvent<any>): void {
    if (event.value) {
      const date = this.setDate(event.value);

      if (this.onChange) {
        this.onChange(date);
      }

      this.checkInvalid();
      this.control?.resetErrors();

      super.writeValue(date);
    }
  }

  writeValue(value: string): void {
    this.checkInvalid();
    this.control?.resetErrors();

    const date = moment(value);

    if (date.isValid()) {
      if (this.datepickerRef) {
        this.datepickerRef.startAt = date;
        this.datepickerRef.select(date);
      }

      if (this.datepickerInputRef) {
        this.datepickerInputRef.nativeElement.value = date.format(environment.datePickerFormat);
      }

      const isoDate = this.setDate(date);

      super.writeValue(isoDate);
    } else {
      this.resetDatepicker();
    }
  }

  asFormsControl(): DatepickerControl {
    if (!this.control) {
      this.control = new DatepickerControl({
        name: '',
        value: '',
        placeholder: '',
        validators: [],
        componentType: 0,
        invalid: false,
        errors: [],
        disabled: false,
        minDate: null,
        maxDate: null,
        more: [],
      });
    }

    if ((typeof this.controlPlaceHolder !== 'undefined') && this.control) {
      this.control.placeholder = this.controlPlaceHolder;
    }

    return (this.control as DatepickerControl);
  }
}
